import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    BonusCommissionCreateData,
    BonusCommissionRequest,
    BonusCommissionResponse,
    BonusCommissionSliceModel,
    BonusCommissionTableResponse,
    BonusCommissionsListResponse,
} from "./bonus-commission-interfaces";
import { ErrorResponse, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetAllBonusesCommissions = createAsyncThunk<BonusCommissionTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/GetAllBonusesCommissions',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/all', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as BonusCommissionsListResponse;
            return responseModel.bonusesAndCommissions;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetMyBonusesCommissions = createAsyncThunk<BonusCommissionTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/GetMyBonusesCommissions',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/get-my', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as BonusCommissionsListResponse;
            return responseModel.bonusesAndCommissions;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetBonusCommission = createAsyncThunk<BonusCommissionResponse, string, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/GetBonusCommission',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/get?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as BonusCommissionResponse;
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateBonusCommission = createAsyncThunk<MainResponse, BonusCommissionRequest, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/CreateBonusCommission',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            dispatch(GetAllBonusesCommissions());
            dispatch(GetMyBonusesCommissions());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Bonus/commission created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateBonusCommission = createAsyncThunk<MainResponse, BonusCommissionRequest, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/UpdateBonusCommission',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            dispatch(GetAllBonusesCommissions());
            dispatch(GetMyBonusesCommissions());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Bonus/commission updated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetBonusCommissionCreateData = createAsyncThunk<BonusCommissionCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/GetBonusCommissionCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/getcreatedata', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            return await response.json() as BonusCommissionCreateData;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetAllInactiveBonusesCommissions = createAsyncThunk<BonusCommissionTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/GetAllInactiveBonusesCommissions',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/getAllInactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as BonusCommissionsListResponse;
            return responseModel.bonusesAndCommissions;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ReactivateBonusCommission = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/ReactivateBonusCommission',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetAllInactiveBonusesCommissions());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Bonus/commission reactivated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateBonusCommission = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/BonusCommissionSlice/DeactivateBonusCommission',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/bonusAndCommission/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetAllBonusesCommissions());
            dispatch(GetMyBonusesCommissions());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Bonus/commission deactivated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: BonusCommissionSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    message: '',
    showNotification: false,
    listOfAllBonusesCommissions: [],
    listOfMyBonusesCommissions: [],
    listOfAllInactiveBonusesCommissions: [],
    reloadData: false,
    bonusCommission: null,
    createData: null,
    permissions: {
        createEditTenants: [],
        reactivateDeactivateTenants: [],
        deleteTenants: [],
    },
};

export const BonusCommissionSlice = createSlice({
    name: 'BonusCommissionSlice',
    initialState: initialValue,
    reducers: {
        resetBonusCommissionSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.reloadData = false;
            state.showNotification = false;
            state.message = '';
            state.listOfAllBonusesCommissions = [];
            state.listOfMyBonusesCommissions = [];
            state.listOfAllInactiveBonusesCommissions = [];
            state.bonusCommission = null;
            state.createData = null;
        },
        resetBonusCommission: (state) => {
            state.bonusCommission = null;
            state.isModalLoading = false;
            state.reloadData = false;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.reloadData = false;
        },
    },
    extraReducers(builder) {
        // GetAllBonusesCommissions
        builder
            .addCase(GetAllBonusesCommissions.fulfilled, (state, action: PayloadAction<BonusCommissionTableResponse[]>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfAllBonusesCommissions = action.payload;
            })
            .addCase(GetAllBonusesCommissions.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllBonusesCommissions.pending, state => {
                state.isLoading = true;
            });
        // GetMyBonusesCommissions
        builder
            .addCase(GetMyBonusesCommissions.fulfilled, (state, action: PayloadAction<BonusCommissionTableResponse[]>) => {
                state.isLoading = false;
                state.listOfMyBonusesCommissions = action.payload;
            })
            .addCase(GetMyBonusesCommissions.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetMyBonusesCommissions.pending, state => {
                state.isLoading = true;
            });
        // GetBonusCommission
        builder
            .addCase(GetBonusCommission.fulfilled, (state, action: PayloadAction<BonusCommissionResponse>) => {
                state.isModalLoading = false;
                state.message = '';
                state.bonusCommission = action.payload;
            })
            .addCase(GetBonusCommission.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetBonusCommission.pending, state => {
                state.isModalLoading = true;
            });
        // GetBonusCommissionCreateData
        builder
            .addCase(GetBonusCommissionCreateData.fulfilled, (state, action: PayloadAction<BonusCommissionCreateData>) => {
                state.isModalLoading = false;
                state.createData = action.payload;
            })
            .addCase(GetBonusCommissionCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetBonusCommissionCreateData.pending, state => {
                state.isModalLoading = true;
            });
        // CreateBonusCommission
        builder
            .addCase(CreateBonusCommission.fulfilled, (state) => {
                state.isModalLoading = false;
                state.reloadData = true;
            })
            .addCase(CreateBonusCommission.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateBonusCommission.pending, state => {
                state.isModalLoading = true;
            });
        // UpdateBonusCommission
        builder
            .addCase(UpdateBonusCommission.fulfilled, (state, action) => {
                const req = action.meta.arg;
                if (req.id) {
                    const typeName = state.createData?.type.find(t => t.value === req.type)?.label;
                    const paymentIntervalName = state.createData?.paymentInterval.find(p => p.value === req.paymentInterval)?.label;
                    const update = (r: BonusCommissionTableResponse) =>
                        r.id === req.id
                            ? {
                                ...r,
                                type: req.type,
                                typeName: typeName ?? r.typeName,
                                paymentInterval: req.paymentInterval,
                                paymentIntervalName: paymentIntervalName ?? r.paymentIntervalName,
                                from: req.from as Date,
                                to: req.to as Date,
                                amount: req.amount,
                                currency: req.currency,
                                comment: req.comment,
                            }
                            : r;
                    state.listOfAllBonusesCommissions = state.listOfAllBonusesCommissions.map(update);
                    state.listOfMyBonusesCommissions = state.listOfMyBonusesCommissions.map(update);
                }
                state.isModalLoading = false;
                state.reloadData = true;
            })
            .addCase(UpdateBonusCommission.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateBonusCommission.pending, state => {
                state.isModalLoading = true;
            });
        // GetAllInactiveBonusesCommissions
        builder
            .addCase(GetAllInactiveBonusesCommissions.fulfilled, (state, action: PayloadAction<BonusCommissionTableResponse[]>) => {
                state.isLoading = false;
                state.listOfAllInactiveBonusesCommissions = action.payload;
            })
            .addCase(GetAllInactiveBonusesCommissions.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllInactiveBonusesCommissions.pending, state => {
                state.isLoading = true;
            });
        // ReactivateBonusCommission
        builder
            .addCase(ReactivateBonusCommission.fulfilled, (state) => {
                state.isLoading = false;
                state.reloadData = true;
            })
            .addCase(ReactivateBonusCommission.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(ReactivateBonusCommission.pending, state => {
                state.isLoading = true;
            });
        // DeactivateBonusCommission
        builder
            .addCase(DeactivateBonusCommission.fulfilled, (state, action) => {
                const id = action.meta.arg;
                const update = (r: BonusCommissionTableResponse) =>
                    r.id === id ? { ...r, status: 'Canceled' } : r;
                state.listOfAllBonusesCommissions = state.listOfAllBonusesCommissions.map(update);
                state.listOfMyBonusesCommissions = state.listOfMyBonusesCommissions.map(update);

                state.isLoading = false;
                state.reloadData = true;
            })
            .addCase(DeactivateBonusCommission.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateBonusCommission.pending, state => {
                state.isLoading = true;
            });
    },
});

export const { resetBonusCommission, resetBonusCommissionSlice, resetStatusCode } = BonusCommissionSlice.actions;
export default BonusCommissionSlice.reducer;
